import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import values from 'lodash/values';
import { createTheme, ThemeProvider, StyledEngineProvider, adaptV4Theme } from '@mui/material/styles';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { grey } from '@mui/material/colors';

import { theme } from 'theme';
import { saveQuestionnaire, newsCentreUserDataService } from 'services';
import { savePreferencesAction } from 'store/newsFeed/newsFeedActions';
import { useQuestionnaire } from 'shared/hooks';
import { getNewsFeedPreferences, getFlattenedIndustries, getParsedRegions } from 'selectors/common';
import { getAuthDataUserId } from 'selectors/auth';
import { newsletterFrequencies } from 'shared/constants/news/newsletterFrequencies';
import { newsToArtifactTypes } from 'shared/constants/news/newsSettings';
import { DialogTitleWithClose } from 'ui';
import { initialSettingsParser } from 'shared/parsers';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import QuestionnaireStepper from './QuestionnaireStepper';
import QuestionnaireNextButton from './QuestionnaireNextButton';
import QuestionnairePreviousButton from './QuestionnairePreviousButton';
import QuestionnaireStepHeader from './QuestionnaireStepHeader';
import QuestionnaireFormFieldsBuilder from './QuestionnaireFormFieldsBuilder';
import QuestionnaireContext from './QuestionnaireContext';
import QuestionnaireIntro from './QuestionnaireIntro';

const formTheme = createTheme(adaptV4Theme({
  overrides: {
    MuiToggleButtonGroup: {
      grouped: {
        'button&:not(:first-child), button&:not(:last-child)': {
          borderRadius: 10,
          border: 'none',
          '&.sub-button': {
            border: `1px solid ${theme.palette.primary.main}`,
          },
        },
      },
    },
    MuiToggleButton: {
      root: {
        border: 'none',
        color: theme.palette.custom.black,
        boxShadow: `1px 3px 7px ${grey[400]}`,
        borderRadius: 10,
        'button&:hover': {
          backgroundColor: theme.palette.custom.white,
          color: theme.palette.custom.black,
        },
        '&.Mui-selected, button&.Mui-selected:hover': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.custom.white,
        },
        '&.sub-button': {
          boxShadow: 'none',
        },
      },
    },
  },
}));

const QuestionnaireForm = ({ data, onClose, qualificationId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userId = useSelector(getAuthDataUserId);
  const newsFeedPreferences = useSelector(getNewsFeedPreferences);
  const industriesList = useSelector(getFlattenedIndustries);
  const { data: countriesList } = useSelector(getParsedRegions);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const {
    setAnswer,
    pushAnswer,
    removeAnswer,
    removeAnswers,
    setParam,
    answers,
    params,
  } = useQuestionnaire();

  const showPreviousButton = currentStep > 0;
  const showSubmitButton = currentStep === data.length - 1;
  const cleanStepAnswers = options => {
    options.forEach(option => {
      removeAnswers(option.questionId);

      if (option.options) {
        cleanStepAnswers(values(option.options));
      }
    });
  };
  const handleNextClick = () => {
    setCurrentStep(currentStep + 1);
    cleanStepAnswers(data[currentStep + 1].questions);
    setParam('isHeaderVisible', true);
    setParam('isValidate', false);
  };
  const handlePreviousClick = () => {
    setCurrentStep(currentStep - 1);
    cleanStepAnswers(data[currentStep].questions);
    cleanStepAnswers(data[currentStep - 1].questions);
    setParam('isHeaderVisible', true);
    setParam('isValidate', false);
  };
  const handleSubmit = () => {
    setSubmitted(true);
    saveQuestionnaire(answers, qualificationId, userId)
      .then(() => {
        newsCentreUserDataService(userId).then(({ industries, regions, settings }) => {
          const initialSettings = initialSettingsParser(newsFeedPreferences);
          let updatedSettings = { ...initialSettings };

          if (settings.customize) {
            updatedSettings = {
              ...initialSettings,
              newsFeed: {
                ...initialSettings.newsFeed,
                industryTypes: industriesList.filter(({ industryName }) =>
                  industries.includes(industryName),
                ),
                countriesList: countriesList
                  .filter(({ continent }) => regions.includes(continent))
                  .map(({ iso }) => iso),
              },
            };
          }

          if (settings.notifications) {
            updatedSettings = {
              ...updatedSettings,
              newsletter: {
                ...updatedSettings.newsletter,
                frequency: newsletterFrequencies.WEEKLY,
                newsTypes: [
                  ...newsToArtifactTypes.RATING_NEWS,
                  ...newsToArtifactTypes.RESEARCH,
                  ...newsToArtifactTypes.COMPANY_NEWS,
                ],
              },
            };
          }

          if (updatedSettings) {
            dispatch(savePreferencesAction(updatedSettings));
          }

          dispatch(
            addNotificationAction({
              variant: 'success',
              message: t('Your answers has been saved.'),
            }),
          );
          onClose();
        });
      })
      .catch(() => {
        dispatch(
          addNotificationAction({
            variant: 'error',
            message: t('Failed to save answers. Please try again or contact.'),
          }),
        );
        onClose();
      });
  };

  const formFields = data[currentStep].questions.map(
    ({ questionLabel, questionSubLabel, type, options, questionId }) => {
      const handleRemoveValue = value => removeAnswer(questionId, value);

      return (
        <React.Fragment key={questionId}>
          {params.isHeaderVisible && (
            <QuestionnaireStepHeader label={questionLabel} subLabel={questionSubLabel} />
          )}
          <StyledEngineProvider injectFirst>
            <ThemeProvider
              theme={{
                ...theme,
                overrides: {
                  ...theme.overrides,
                  ...formTheme.overrides,
                },
              }}
            >
              <QuestionnaireFormFieldsBuilder
                type={type}
                options={values(options)}
                questionId={questionId}
                onSetValue={setAnswer}
                onRemoveValue={handleRemoveValue}
                onRemoveValues={removeAnswers}
                onPushValue={pushAnswer}
                step={currentStep}
              />
            </ThemeProvider>
          </StyledEngineProvider>
        </React.Fragment>
      );
    },
  );

  return (
    <>
      <DialogTitleWithClose onClose={onClose} />
      <DialogContent>
        <QuestionnaireContext.Provider value={{ setParam }}>
          {!showIntro ? (
            <QuestionnaireStepper stepsNumber={data.length} current={currentStep} />
          ) : null}
          {showIntro ? <QuestionnaireIntro /> : formFields}
        </QuestionnaireContext.Provider>
      </DialogContent>
      <DialogActions>
        {showPreviousButton && (
          <QuestionnairePreviousButton onClick={handlePreviousClick}>
            {t('Previous')}
          </QuestionnairePreviousButton>
        )}
        {showSubmitButton ? (
          <QuestionnaireNextButton onClick={handleSubmit} disabled={submitted}>
            {t('Submit')}
          </QuestionnaireNextButton>
        ) : null}
        {showIntro ? (
          <QuestionnaireNextButton onClick={() => setShowIntro(false)}>
            {t('Next')}
          </QuestionnaireNextButton>
        ) : null}
        {!showIntro && !showSubmitButton ? (
          <QuestionnaireNextButton onClick={handleNextClick} disabled={!params.isValidate}>
            {t('Next')}
          </QuestionnaireNextButton>
        ) : null}
      </DialogActions>
    </>
  );
};

QuestionnaireForm.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      stepId: PropTypes.number,
      questions: PropTypes.array,
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  qualificationId: PropTypes.number.isRequired,
};

export default QuestionnaireForm;
